import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useContext, useEffect } from 'react';
import { Container } from 'inversify';
import { Await } from './await';
import { ContainerContext } from './common';
import { defaultErrorFallback, defaultLoadingFallback } from './fallbacks';
import { isStore } from './store';
function isClassProviderConfigEntryUseClass(entry) {
    return Object.hasOwn(entry, 'provide') && Object.hasOwn(entry, 'useClass');
}
function isValueProviderConfigEntry(entry) {
    return Object.hasOwn(entry, 'provide') && Object.hasOwn(entry, 'useValue');
}
function isClassProviderConfigEntryToken(entry) {
    return (Object.hasOwn(entry, 'provide') &&
        !Object.hasOwn(entry, 'useClass') &&
        !Object.hasOwn(entry, 'useValue'));
}
function isProviderConfigEntryInheritable(entry) {
    return (isClassProviderConfigEntryUseClass(entry) ||
        isClassProviderConfigEntryToken(entry) ||
        isValueProviderConfigEntry(entry));
}
function getProvideTokenWithClass(entry) {
    if (isClassProviderConfigEntryUseClass(entry)) {
        return [entry.provide, entry.useClass];
    }
    if (isClassProviderConfigEntryToken(entry)) {
        return [entry.provide, entry.provide];
    }
    if (isValueProviderConfigEntry(entry)) {
        return [undefined, undefined];
    }
    return [entry, entry];
}
function getStoreInstances(container, { singletons = [], instances = [] }) {
    return [...singletons, ...instances].reduce((out, entry) => {
        const [provide, useClass] = getProvideTokenWithClass(entry);
        if (provide && useClass && container.isCurrentBound(provide) && isStore(useClass)) {
            out.push(container.get(provide));
        }
        return out;
    }, []);
}
const bindEntry = (container, checkInherit) => (entry) => {
    if (checkInherit &&
        isProviderConfigEntryInheritable(entry) &&
        entry.inherit &&
        container.isBound(entry.provide)) {
        return undefined;
    }
    if (isClassProviderConfigEntryUseClass(entry)) {
        return container.bind(entry.provide).to(entry.useClass);
    }
    if (isClassProviderConfigEntryToken(entry)) {
        return container.bind(entry.provide).to(entry.provide);
    }
    if (isValueProviderConfigEntry(entry)) {
        // using toDynamicValue to return BindingInSyntax
        return container.bind(entry.provide).toDynamicValue(() => entry.useValue);
    }
    return container.bind(entry).to(entry);
};
const unbindEntry = (container) => (entry) => {
    if (isProviderConfigEntryInheritable(entry) && !container.isBound(entry.provide)) {
        return;
    }
    if (isClassProviderConfigEntryUseClass(entry) ||
        isValueProviderConfigEntry(entry) ||
        isClassProviderConfigEntryToken(entry)) {
        return container.unbind(entry.provide);
    }
    return container.unbind(entry);
};
function bindEntries(container, { singletons = [], instances = [] }) {
    singletons.map(bindEntry(container, true)).forEach(binding => {
        binding === null || binding === void 0 ? void 0 : binding.inSingletonScope();
    });
    instances.forEach(bindEntry(container, false));
}
function unbindEntries(container, { singletons = [], instances = [] }) {
    [...singletons, ...instances].forEach(unbindEntry(container));
}
function initializeStores(stores, onError) {
    var _a;
    const promises = [];
    for (const instance of stores) {
        let promise;
        try {
            promise = (_a = instance.initialize) === null || _a === void 0 ? void 0 : _a.call(instance);
        }
        catch (e) {
            onError(e);
        }
        if (promise && typeof promise.catch === 'function') {
            promises.push(promise.catch(onError));
        }
    }
    return promises;
}
function disposeStores(stores) {
    var _a;
    for (const instance of stores) {
        (_a = instance.dispose) === null || _a === void 0 ? void 0 : _a.call(instance);
    }
}
export const Provider = ({ singletons, instances, loadingFallback = defaultLoadingFallback, errorFallback = defaultErrorFallback, children, }) => {
    const parentContainer = useContext(ContainerContext);
    const [container, setContainer] = useState();
    const [promises, setPromises] = useState([]);
    const [error, setError] = useState(false);
    useEffect(() => {
        const container = new Container();
        container.parent = parentContainer;
        setContainer(container);
        const handleError = (e) => {
            setError(true);
            // Rethrow asynchronously for global error handler
            setTimeout(() => {
                throw e;
            });
        };
        bindEntries(container, { singletons, instances });
        const promises = initializeStores(getStoreInstances(container, { singletons, instances }), handleError);
        setPromises(promises);
        return () => {
            const storeInstances = getStoreInstances(container, { singletons, instances });
            unbindEntries(container, { singletons, instances });
            disposeStores(storeInstances);
            setContainer(undefined);
            setPromises([]);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    if (!container) {
        return null;
    }
    const getContent = () => {
        if (error) {
            return errorFallback;
        }
        if (promises.length) {
            return (_jsx(Await, { promises: promises, fallback: loadingFallback, children: children }));
        }
        return children;
    };
    return _jsx(ContainerContext.Provider, { value: container, children: getContent() });
};
//# sourceMappingURL=provider.js.map